import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  InputLabel,
  Typography,
} from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'

import { ChatSettings } from 'api/queries/chatSettings/types'
import { UpdateChatSettingsVariables } from 'api/mutations/chatSettings/types'
import { TextField } from 'components/inputs/TextField/TextField'
import { PasswordField } from 'components/inputs/PasswordField/PasswordField'
import { DeleteModal } from 'components/modals/DeleteModal/DeleteModal'
import { AiChatSettingsSchema } from 'validations/AiChatSettingsSchema'
import * as FormsStyled from '../forms.styles'
import * as Styled from './AiChatSettingsForm.styles'

export interface AiChatSettingsFormFields {
  baseUrl: string
  apiKey: string
}

export interface AiChatSettingsFormProps {
  settings?: ChatSettings
  onSubmit?: (variables: UpdateChatSettingsVariables) => void
  isLoadingData?: boolean
  isError?: boolean
  isLoadingUpdate?: boolean
  isSuccessUpdate?: boolean
}

const AiChatSettingsForm: React.FC<AiChatSettingsFormProps> = ({
  settings,
  onSubmit = () => {},
  isLoadingData,
  isError,
  isLoadingUpdate,
  isSuccessUpdate,
}) => {
  const { t } = useTranslation()
  const [isRemoveKeyModalOpen, setIsRemoveKeyModalOpen] = useState(false)
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    dirty,
    isValid,
    resetForm,
  } = useFormik<AiChatSettingsFormFields>(
    {
      initialValues: {
        baseUrl: settings?.baseUrl ?? '',
        apiKey: '',
      },
      validationSchema: AiChatSettingsSchema,
      onSubmit: (formValues) => onSubmit({
        baseUrl: formValues.baseUrl.trim(),
        apiKey: formValues.apiKey === '' ? null : formValues.apiKey,
        clearApiKey: false,
      }),
      enableReinitialize: true,
    }
  )

  useEffect(() => {
    if (isSuccessUpdate) {
      resetForm()
      setIsRemoveKeyModalOpen(false)
    }
  }, [isSuccessUpdate, resetForm])

  const storedBaseUrl = settings?.baseUrl

  const handleRemoveKeyConfirm = () => {
    if (!storedBaseUrl) {
      return
    }
    onSubmit({ baseUrl: storedBaseUrl, apiKey: null, clearApiKey: true })
  }

  const apiKeyHelperText = touched.apiKey && errors.apiKey
    ? t(errors.apiKey)
    : settings?.apiKeySet && t('aiSettings.form.apiKeyHint')

  return (
    <>
      <FormsStyled.Form onSubmit={handleSubmit}>
        <Card variant='outlined'>
          <CardHeader
            title={t('aiSettings.title')}
            titleTypographyProps={{ variant: 'h3' }}
            subheader={t('aiSettings.description')}
            action={settings && (settings.configured ? (
              <Styled.ConfiguredChip
                id='configurationStatus'
                variant='outlined'
                size='small'
                icon={<CheckCircleOutlineIcon />}
                label={t('aiSettings.form.status.configured')}
              />
            ) : (
              <Styled.NotConfiguredChip
                id='configurationStatus'
                variant='outlined'
                size='small'
                icon={<ErrorOutlineIcon />}
                label={t('aiSettings.form.status.notConfigured')}
              />
            ))}
          />
          <CardContent>
            {isError && (
              <FormsStyled.ErrorMessage>
                {t('common.errors.generic')}
              </FormsStyled.ErrorMessage>
            )}
            {isLoadingData ? (
              <FormsStyled.CenteredContainer>
                <CircularProgress />
              </FormsStyled.CenteredContainer>
            ) : (
              <>
                <Styled.FieldHeader>
                  <InputLabel htmlFor='baseUrl'>
                    {t('aiSettings.form.baseUrl')}
                  </InputLabel>
                </Styled.FieldHeader>
                <TextField
                  id='baseUrl'
                  name='baseUrl'
                  type='text'
                  errorMessage={t(errors.baseUrl || '')}
                  touched={touched.baseUrl}
                  value={values.baseUrl}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <Styled.FieldHeader>
                  <InputLabel htmlFor='apiKey'>
                    {t('aiSettings.form.apiKey')}
                  </InputLabel>
                  {settings?.apiKeySet ? (
                    <Styled.KeyStoredStatus variant='body2' id='apiKeyStatus'>
                      <CheckCircleOutlineIcon fontSize='small' />
                      {t('aiSettings.form.apiKeyStatus.set')}
                    </Styled.KeyStoredStatus>
                  ) : (
                    <Typography variant='body2' color='textSecondary' id='apiKeyStatus'>
                      {t('aiSettings.form.apiKeyStatus.notSet')}
                    </Typography>
                  )}
                </Styled.FieldHeader>
                <PasswordField
                  id='apiKey'
                  name='apiKey'
                  placeholder={t(settings?.apiKeySet
                    ? 'aiSettings.form.apiKeyPlaceholderReplace'
                    : 'aiSettings.form.apiKeyPlaceholderNew')}
                  autoComplete='new-password'
                  errorMessage={t(errors.apiKey || '')}
                  touched={touched.apiKey}
                  helperText={apiKeyHelperText}
                  value={values.apiKey}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </>
            )}
          </CardContent>
          <Divider />
          <Styled.Actions disableSpacing>
            {settings?.apiKeySet && (
              <Styled.RemoveKeyButton
                type='button'
                variant='outlined'
                startIcon={<DeleteOutlineIcon />}
                disabled={!storedBaseUrl || isLoadingUpdate}
                onClick={() => setIsRemoveKeyModalOpen(true)}
              >
                {t('aiSettings.form.removeApiKey')}
              </Styled.RemoveKeyButton>
            )}
            <Styled.SaveButton
              type='submit'
              disabled={!dirty || !isValid || isLoadingUpdate}
              isLoading={isLoadingUpdate}
            >
              {t('common.saveChanges')}
            </Styled.SaveButton>
          </Styled.Actions>
        </Card>
      </FormsStyled.Form>
      <DeleteModal
        open={isRemoveKeyModalOpen}
        title={t('aiSettings.removeApiKeyModal.title')}
        subText={t('aiSettings.removeApiKeyModal.subText')}
        onClose={() => setIsRemoveKeyModalOpen(false)}
        onSave={handleRemoveKeyConfirm}
        isLoading={isLoadingUpdate}
      />
    </>
  )
}

export { AiChatSettingsForm }
